import { Menu, LogOut, Shield, UserCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useAuth } from "../contexts/AuthContext";
import { toTitleCase } from "../lib/utils";
import { Link, useNavigate } from "react-router-dom";
import { ThemeToggle } from "./ThemeToggle";

export function Header({ onMenuClick }) {
  const { user, logout, homePath, portal } = useAuth();
  const navigate = useNavigate();

  const getUserName = () => toTitleCase(user?.full_name || user?.name || "User");

  const getUserImage = () => {
    const imageUrl = user?.picture_path || user?.avatar;
    if (!imageUrl || imageUrl === "null" || imageUrl === "undefined") return undefined;
    return String(imageUrl).trim() || undefined;
  };

  const getUserInitials = () => {
    const name = user?.full_name || user?.name;
    if (name) {
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    return "U";
  };

  const portalTitle =
    portal === "admin"
      ? "Admin Portal"
      : portal === "ftm"
        ? "FTM Portal"
        : portal === "church"
          ? "Church Portal"
          : portal === "setmember"
            ? "Set Member Portal"
            : portal === "setleader"
              ? "Set Leader Portal"
              : "CELSZC Portal";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <header className="fixed top-0 left-0 right-0 h-[calc(4rem+env(safe-area-inset-top))] bg-card/95 backdrop-blur-md border-b border-border z-50 shadow-sm overflow-x-hidden pt-[env(safe-area-inset-top)]">
      <div className="flex items-center justify-between h-16 px-3 sm:px-4 lg:px-6 gap-2 min-w-0">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden min-h-11 min-w-11"
            onClick={onMenuClick}
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" />
          </Button>

          <Link to={homePath || "/profile"} className="flex items-center gap-3 min-w-0">
            <div className="hidden sm:flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-[#1E74FD] to-blue-600 text-white shrink-0 shadow-md shadow-[#1E74FD]/25">
              <Shield className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-bold text-foreground truncate">
                {portalTitle}
              </h1>
              <p className="hidden sm:block text-xs text-muted-foreground">
                Christ Embassy Lagos Sub Zone C
              </p>
            </div>
          </Link>
        </div>

        <div className="flex items-center gap-1 sm:gap-3 shrink-0">
          <ThemeToggle className="min-h-11 min-w-11" />

          <div className="flex items-center gap-2 sm:gap-3 sm:pl-3 sm:border-l border-border">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-lg p-1.5 min-h-11 hover:bg-accent/60 transition-colors"
                  aria-label="Account menu"
                >
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={getUserImage()} />
                    <AvatarFallback className="bg-gradient-to-br from-[#1E74FD] to-blue-600 text-white text-sm font-semibold">
                      {getUserInitials()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-foreground leading-tight">
                      {getUserName()}
                    </p>
                    <p className="text-xs text-muted-foreground leading-tight">
                      {user?.role_display || user?.role || "Member"}
                    </p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-medium">{getUserName()}</div>
                  <div className="text-xs text-muted-foreground font-normal">
                    {user?.email}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/profile" className="cursor-pointer">
                    <UserCircle className="mr-2 h-4 w-4" />
                    Profile
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="hidden sm:inline-flex text-[#1E74FD] hover:text-[#1E74FD]/80 hover:bg-[#1E74FD]/10 min-h-11 min-w-11"
              aria-label="Sign out"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}
